<template>
  <CurrentEventDataProvider v-slot="{ title, data, features }">
    <div class="h-full w-full flex flex-col bg-white text-dark px-4">
      <!-- Navbar -->
      <div
        class="navbar pt-4 flex justify-between items-center font-bold bg-white sticky top-0 z-30"
      >
        <!-- Logo & Event name -->
        <div class="logo flex items-center">
          <img
            :src="data.logoUrl"
            :alt="data.logoUrl && $t('Views.Login.eventLogo')"
            class="h-5 lg:h-10 w-auto"
          />
        </div>

        <!-- Support & Logout -->
        <div>
          <button
            v-show="chatWidgetLoaded"
            v-if="features.stageSupport"
            class="button button-primary button-xs text-xxs md:text-xs sm:inline-block"
            :aria-label="$t('Views.Login.support')"
            @click="toggleLiveChatWindow"
          >
            {{ $t('Views.Login.support') }}
          </button>
        </div>
      </div>

      <!-- Content -->
      <div
        class="flex flex-col flex-1 bg-light mt-4 md:mb-8 md:ml-8 md:mr-8 items-center justify-center"
        style="
          color: var(--login-color-text);
          background-color: var(--login-background-color);
        "
      >
        <div class="text-center mb-2 md:mb-8">
          <h1 class="font-bold text-md md:text-lg mb-4">
            {{ title }}
          </h1>
          <div
            class="text-xs md:text-base px-6 text-center html-content"
            v-html="data.loginCaption"
          />
          <h2 class="font-bold text-base md:text-md">
            {{ $t('Views.Login.logInToEventName', { name: data.eventName }) }}
          </h2>
        </div>

        <Card v-if="data.active === '1'" class="p-8 md:p-10 md:w-2/5">
          <form
            method="post"
            autocapitalize="none"
            @submit.prevent="submitForm"
          >
            <div class="space-y-4">
              <div v-if="loginFailed" class="flex-1">
                <transition name="fade">
                  <span class="form-error text-sm font-bold">
                    <i class="fas fa-exclamation-circle mr-1" />
                    {{ $t('Views.Login.Otp.linkExpired') }}
                  </span>
                </transition>
              </div>
              <div
                class="text-dark text-xs md:text-sm lg:text-base select-none space-y-4"
              >
                <!-- Platform visibility -->
                <div
                  class="hidden md:flex flex-col space-y-2"
                  v-if="isWebappEnabled && (isLobbyEnabled || isStageEnabled)"
                >
                  <div v-if="shouldRenderError" class="flex-1">
                    <transition name="fade">
                      <span
                        class="form-error flex text-sm font-bold items-center"
                      >
                        <i class="fas fa-exclamation-circle mr-1" />
                        <p>{{ $t('Views.Login.selectAnOption') }}</p>
                      </span>
                    </transition>
                  </div>
                  <div>
                    <span>{{ $t('Views.Login.platformToggleLabel') }}</span>
                  </div>
                  <div class="flex space-x-8">
                    <div class="space-x-2">
                      <input
                        type="radio"
                        v-model="form.platform"
                        :value="false"
                        id="physicalRadio"
                        :aria-label="$t('Views.Login.physical')"
                      />
                      <label for="physicalRadio">{{
                        $t('Views.Login.physical')
                      }}</label>
                    </div>
                    <div class="space-x-2">
                      <input
                        type="radio"
                        v-model="form.platform"
                        :value="true"
                        id="onlineRadio "
                        :aria-label="$t('Views.Login.online')"
                      />
                      <label for="onlineRadio">{{
                        $t('Views.Login.online')
                      }}</label>
                    </div>
                  </div>
                </div>
                <!-- Profile visibility -->
                <div
                  class="space-x-2"
                  v-if="
                    data?.profileVisibilities?.includes(2) &&
                    (features.stageTabParticipants ||
                      features.webAppNavigationParticipants)
                  "
                >
                  <div>
                    <ToggleButton
                      :color="{ checked: '#22aaa2', unchecked: '#A6a6a6' }"
                      :width="75"
                      :value="form.visible"
                      :labels="{
                        checked: $t('Views.Login.visible'),
                        unchecked: $t('Views.Login.hidden')
                      }"
                      :aria-label="$t('Views.Login.visibilityToggleLabel')"
                      @input="form.visible = !form.visible"
                    />
                    <span>{{ $t('Views.Login.visibilityToggleLabel') }}</span>
                  </div>
                  <div class="mt-2">
                    <ToggleButton
                      :color="{ checked: '#22aaa2', unchecked: '#A6a6a6' }"
                      :width="75"
                      :value="form.emailAndPhoneVisible"
                      :labels="{
                        checked: $t('Views.Login.visible'),
                        unchecked: $t('Views.Login.hidden')
                      }"
                      :aria-label="$t('Views.Login.emailAndPhoneNumberToggle')"
                      @input="
                        form.emailAndPhoneVisible = !form.emailAndPhoneVisible
                      "
                      :disabled="!form.visible"
                    />
                    <span>{{
                      $t('Views.Login.emailAndPhoneNumberToggle')
                    }}</span>
                  </div>
                </div>
              </div>
              <div class="flex items-baseline justify-between flex-col">
                <div
                  v-if="loginFailed"
                  class="w-full flex flex-col-reverse md:flex-row justify-center"
                >
                  <a
                    :href="'request/' + token"
                    class="whitespace-nowrap button button-primary mt-2 md:mt-0"
                    :aria-label="$t('Views.Login.Otp.requestNewLink')"
                  >
                    <span
                      v-if="isLoggingIn"
                      class="inline-block"
                      :aria-label="$t('Views.Login.loggingIn')"
                    >
                      {{ $t('Views.Login.loggingIn') }}
                      <i class="fal ml-2 fa-circle-notch fa-spin" />
                    </span>
                    <span
                      v-else
                      class="inline-block"
                      :aria-label="$t('Views.Login.Otp.requestNewLink')"
                    >
                      {{ $t('Views.Login.Otp.requestNewLink') }}
                    </span>
                  </a>
                </div>
                <div
                  class="w-full flex flex-col-reverse md:flex-row justify-center"
                >
                  <TappinButton
                    type="submit"
                    :disabled="isLoggingIn || loading"
                    class="whitespace-nowrap button button-primary mt-2 md:mt-0"
                    :aria-label="
                      isLoggingIn
                        ? $t('Views.Login.loggingIn')
                        : $t('Views.Login.login')
                    "
                    :icon="isLoggingIn ? 'circle-notch' : ''"
                    :custom_caption="
                      isLoggingIn
                        ? $t('Views.Login.loggingIn')
                        : $t('Views.Login.login')
                    "
                  />
                </div>
              </div>
            </div>
          </form>
        </Card>
        <Card v-else class="p-10 lg:w-2/5">
          <span class="text-center" :aria-label="$t('Views.Login.eventClosed')">
            <i class="fas fa-exclamation-circle mr-1" aria-hidden="true" />
            {{ $t('Views.Login.eventClosed') }}
          </span>
        </Card>
      </div>
    </div>
  </CurrentEventDataProvider>
</template>

<script>
import { ToggleButton } from 'vue-js-toggle-button';

import { mapActions, mapGetters } from 'vuex';
import Card from '@/components/shared/Card';
import ApiClient from '@/client';
import TermsOfServiceModal from '@/components/TermsOfServiceModal';
import { togglesSupportChat } from '@/mixins';
import TwoFactorModal from '@/components/TwoFactorModal';
import TappinButton from '@/components/buttons/TappinButton';
import {
  redirectAuthenticatedUser,
  setStoredParticipationType,
  participationFromPlatformBoolean,
  phoneLoginPlatform
} from '@/helpers/redirection-helpers';

export default {
  name: 'PasswordlessLogin',
  components: {
    Card,
    ToggleButton,
    TappinButton
  },
  mixins: [togglesSupportChat],
  created() {
    this.token = encodeURIComponent(this.$route.params.payload);
  },
  data() {
    return {
      token: null,
      loading: false,
      loginFailed: false,
      form: {
        remember: false,
        visible: false,
        platform: null,
        emailAndPhoneVisible: false
      },
      tosText: null,
      twoFact: null,
      twoFactPinId: null
    };
  },
  computed: {
    ...mapGetters('CurrentEvent', [
      'features',
      'isLobbyEnabled',
      'isStageEnabled',
      'isWebappEnabled',
      'defaultEmailAndPhoneVisibility',
      'defaultProfileVisibility',
      'getId',
      'getTwoFact',
      'features'
    ]),
    ...mapGetters('Authentication', ['isLoggedIn', 'isLoggingIn', 'getUser']),
    ...mapGetters('Terms', ['termsShown']),

    shouldRenderError() {
      return window.innerWidth > 768 && this.form.platform === null;
    }
  },
  mounted() {
    this.form.visible = this.defaultProfileVisibility ?? false;
    this.form.emailAndPhoneVisible = this.defaultEmailAndPhoneVisibility;
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  methods: {
    ...mapActions('Authentication', ['doPasswordlessLogin', 'doLogout']),
    ...mapActions('Terms', ['setTermsAsShown']),
    handleResize() {
      this.$forceUpdate();
    },
    async submitForm(e) {
      this.loginFailed = false;
      this.loading = true;

      if (this.features.twoFactorAuth) {
        // Show twofactor modal screen
        this.showTwoFactorModal();
        return;
      }
      await this.doPasswordlessLogin({
        payload: this.$route.params.payload,
        eventId: this.getId,
        visible: this.form.visible,
        platform: this.form.platform,
        emailAndPhoneVisible: this.form.emailAndPhoneVisible
      });

      return this.isLoggedIn ? this.onLoginSuccess() : this.onLoginFailure();
    },

    showTwoFactorModal() {
      ApiClient.getTwoFactorPin(this.form.email, this.getId).then(
        (response) => {
          if (response.data.status === 'success') {
            this.twoFactPinId = response.data.data.pinId;
            this.$modal.show(
              TwoFactorModal,
              {},
              {
                focusTrap: true,
                clickToClose: false,
                adaptive: true,
                width: '700',
                height: '80%'
              },
              {
                'before-close': (event) => {
                  return event.params.success
                    ? this.onTwoFactorSuccess(event.params.pin)
                    : this.onTwoFactorFailure();
                }
              }
            );
          }
        }
      );
    },

    showTermsOfServiceModal() {
      ApiClient.getTermsOfServiceText(this.form.email, this.getId).then(
        (response) => {
          this.tosText = response.data;
          this.$modal.show(
            TermsOfServiceModal,
            {
              tos: response.data
            },
            {
              focusTrap: true,
              clickToClose: false,
              adaptive: true,
              width: '700',
              height: '80%'
            },
            {
              'before-close': (event) => {
                return event.params.agreed
                  ? this.onTosAgreed()
                  : this.onTosDeclined();
              }
            }
          );
        }
      );
    },

    onLoginSuccess() {
      // Phones never show the physical / online picker: on a hybrid event
      // a phone login is a physical (web app) attendee, as before WCAG.
      this.form.platform = phoneLoginPlatform(this.form.platform);

      if (this.termsShown) {
        this.onTosAgreed();
      } else {
        this.showTermsOfServiceModal();
      }

      if (window.innerWidth <= 768) {
        // Mobile login redirect — same landing as Login.vue: a physical
        // attendee goes to /program, a digital one to /lobby when it's
        // on, else /program.
        const isPhysical = this.form.platform === false;
        const mobileTarget =
          this.isLobbyEnabled && !isPhysical ? 'lobby' : 'program';
        this.$router.push({ name: mobileTarget });
      }
    },

    onTosAgreed() {
      ApiClient.storeTermsOfServiceAcceptance(
        this.form.email,
        this.tosText,
        this.getId
      ).then(() => {
        this.setTermsAsShown();
        this.loading = false;
        this.loginFailed = false;
        // Persist the participation choice so reactive consumers
        // (LiveStream, profile gating, etc.) pick it up everywhere.
        setStoredParticipationType(
          this.getUser?.UserID,
          participationFromPlatformBoolean(this.form.platform)
        );
        redirectAuthenticatedUser(
          this.$router,
          this.isWebappEnabled,
          this.isLobbyEnabled,
          this.isStageEnabled,
          this.form.platform
        );
      });
    },

    onTosDeclined() {
      this.$notify({
        group: 'default',
        title: this.$t('Views.Login.mustAcceptTerms')
      });
    },

    onLoginFailure() {
      console.log('Login failed after two factor');
      this.loading = false;
      this.loginFailed = true;
    },

    onTwoFactorFailure() {
      this.loading = false;
      this.loginFailed = true;
    },

    requestNewLink() {
      this.$router.push({
        name: 'login/otp/request',
        params: {
          payload: encodeURIComponent(this.$route.params.payload)
        }
      });
    },

    async onTwoFactorSuccess(pin) {
      await this.doPasswordlessLogin({
        payload: this.$route.params.payload,
        eventId: this.getId,
        pinId: this.twoFactPinId,
        pin: pin,
        visible: this.form.visible,
        platform: this.form.platform
      });

      return this.isLoggedIn ? this.onLoginSuccess() : this.onLoginFailure();
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  }
};
</script>
